import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "./Reveal";
import { IMAGES } from "@/i18n";

const IMGS = [IMAGES.approachAnalyze, IMAGES.approachPlan, IMAGES.approachBuild, IMAGES.approachAdopt];

export default function Approach() {
  const { t } = useLang();
  const steps = t.approach.steps;
  const railRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: railRef, offset: ["start 65%", "end 65%"] });
  const fillScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section id="approach" className="relative py-28 md:py-40 bg-bg-2/30 border-y border-line">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Sticky left */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <Reveal><Overline>{t.approach.overline}</Overline></Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-head font-bold tracking-tighter text-ink text-[clamp(2.25rem,5vw,4.5rem)] leading-[0.95] mt-5">
                  {t.approach.title}
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-base md:text-lg text-ink-2 leading-relaxed mt-6 max-w-md">
                  {t.approach.subtitle}
                </p>
              </Reveal>
            </div>
          </div>

          {/* Scrolling right with progress rail */}
          <div ref={railRef} className="lg:col-span-7 relative">
            {/* Track + scroll-driven fill (desktop) */}
            <div className="hidden md:block absolute left-[15px] top-4 bottom-4 w-px bg-line" />
            <motion.div
              style={{ scaleY: fillScale }}
              className="hidden md:block absolute left-[15px] top-4 bottom-4 w-px bg-brand origin-top"
            />

            <div className="flex flex-col gap-6 md:pl-16">
              {steps.map((step, i) => (
                <Reveal
                  key={i}
                  delay={i * 0.05}
                  className="group relative rounded-2xl border border-line overflow-hidden bg-bg"
                  data-testid={`approach-step-${i}`}
                >
                  {/* Node on the rail */}
                  <div
                    className="hidden md:flex absolute top-8 items-center justify-center h-8 w-8 rounded-full bg-brand text-white ring-4 ring-bg-2 z-10"
                    style={{ left: "-65px" }}
                    data-testid={`approach-node-${i}`}
                  >
                    <span className="font-mono text-xs font-bold">{step.num}</span>
                  </div>

                  <div className="relative h-52 md:h-64 overflow-hidden">
                    <img
                      src={IMGS[i]}
                      alt={step.title}
                      className="h-full w-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-[opacity,transform] duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/40 to-transparent" />
                    <span className="absolute top-6 left-8 font-head font-black text-white text-6xl md:text-7xl leading-none drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]">
                      {step.num}
                    </span>
                  </div>
                  <div className="p-8 md:p-10 -mt-6 relative">
                    <h3 className="font-head font-bold text-ink text-2xl md:text-3xl tracking-tight">{step.title}</h3>
                    <p className="text-ink-2 leading-relaxed mt-4 max-w-xl">{step.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
